import React, { useEffect, useState, useRef } from 'react';

const RathGame = () => {
  const [playerX, setPlayerX] = useState(150);
  const [chariots, setChariots] = useState([]);
  const [score, setScore] = useState(0);
  const [coins, setCoins] = useState(0); // New coin system
  const [gameOver, setGameOver] = useState(false);
  const [speed, setSpeed] = useState(2);
  const [level, setLevel] = useState(1);
  const [lives, setLives] = useState(3);
  const [powerUps, setPowerUps] = useState([]);
  const [coinsCollected, setCoinsCollected] = useState([]); // Track collected coins
  const [invincible, setInvincible] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [roadWidth, setRoadWidth] = useState(300);

  const gameRef = useRef(null);
  const roadRef = useRef(null);
  const audioRef = useRef(null);

  // Play sound effects
  const playSound = (soundType) => {
    // In a real implementation, you would play actual sound files
    // For now, we'll just log the sound effects
    console.log(`Playing ${soundType} sound`);
  };

  // Responsive resize
  useEffect(() => {
    const handleResize = () => {
      const newWidth = Math.min(window.innerWidth * 0.8, 300);
      setRoadWidth(newWidth);
      setPlayerX(newWidth / 2);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Start game
  const startGame = () => {
    setGameStarted(true);
    setGameOver(false);
    setScore(0);
    setCoins(0);
    setSpeed(2);
    setLevel(1);
    setLives(3);
    setChariots([]);
    setPowerUps([]);
    setCoinsCollected([]);
    setInvincible(false);
    setPlayerX(roadWidth / 2);
    playSound('gameStart');
  };

  // Level up logic
  useEffect(() => {
    if (score > 0 && score % 500 === 0) {
      setLevel(prev => prev + 1);
      setSpeed(prev => Math.min(prev + 0.5, 8));
      playSound('levelUp');
    }
  }, [score]);

  // Score and gradual speed increase
  useEffect(() => {
    if (!gameStarted || gameOver) return;
    const interval = setInterval(() => {
      setScore(s => s + 1);
      if (speed < 8) setSpeed(sp => sp + 0.003);
    }, 100);
    return () => clearInterval(interval);
  }, [gameStarted, gameOver, speed]);

  // Player movement
  useEffect(() => {
    if (!gameStarted) return;
    const handleMove = (e) => {
      if (e.key === 'ArrowLeft') {
        setPlayerX(x => Math.max(roadWidth / 6, x - roadWidth / 6));
        playSound('move');
      }
      if (e.key === 'ArrowRight') {
        setPlayerX(x => Math.min(roadWidth - roadWidth / 6, x + roadWidth / 6));
        playSound('move');
      }
    };
    window.addEventListener('keydown', handleMove);
    return () => window.removeEventListener('keydown', handleMove);
  }, [gameStarted, roadWidth]);

  // Enemy chariot generation
  useEffect(() => {
    if (!gameStarted || gameOver) return;

    const interval = setInterval(() => {
      setChariots((old) => {
        const updated = old
          .map(c => ({ ...c, y: c.y + speed }))
          .filter(c => c.y < 600);

        const spawnRate = Math.max(0.02, 0.04 - (level * 0.005));
        if (Math.random() < spawnRate) {
          // Calculate lane positions based on current road width
          const laneWidth = roadWidth / 3;
          const lanes = [
            laneWidth / 2,  // Left lane
            laneWidth + laneWidth / 2,  // Center lane
            2 * laneWidth + laneWidth / 2  // Right lane
          ];
          const lane = lanes[Math.floor(Math.random() * lanes.length)];
          
          // Randomly select enemy type: asura-avatar or kaurava-chariot
          const enemyTypes = ['asura', 'kaurava'];
          const enemyType = enemyTypes[Math.floor(Math.random() * enemyTypes.length)];
          
          updated.push({
            x: lane,
            y: -100,
            id: Math.random(),
            type: Math.random() > 0.8 ? 'elite' : 'normal',
            enemyType: enemyType
          });
        }
        return updated;
      });
    }, 50);
    return () => clearInterval(interval);
  }, [speed, gameOver, gameStarted, level, roadWidth]);

  // Power-up generation
  useEffect(() => {
    if (!gameStarted || gameOver) return;

    const interval = setInterval(() => {
      if (Math.random() < 0.005) {
        // Calculate lane positions based on current road width
        const laneWidth = roadWidth / 3;
        const lanes = [
          laneWidth / 2,  // Left lane
          laneWidth + laneWidth / 2,  // Center lane
          2 * laneWidth + laneWidth / 2  // Right lane
        ];
        const lane = lanes[Math.floor(Math.random() * lanes.length)];
        
        setPowerUps((old) => [
          ...old,
          {
            x: lane,
            y: -50,
            id: Math.random(),
            type: Math.random() > 0.5 ? 'shield' : 'life'
          }
        ]);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [gameStarted, gameOver, roadWidth]);

  // Coin generation
  useEffect(() => {
    if (!gameStarted || gameOver) return;

    const interval = setInterval(() => {
      if (Math.random() < 0.03) { // 3% chance to spawn a coin each interval
        // Calculate lane positions based on current road width
        const laneWidth = roadWidth / 3;
        const lanes = [
          laneWidth / 2,  // Left lane
          laneWidth + laneWidth / 2,  // Center lane
          2 * laneWidth + laneWidth / 2  // Right lane
        ];
        const lane = lanes[Math.floor(Math.random() * lanes.length)];
        
        setCoinsCollected((old) => [
          ...old,
          {
            x: lane,
            y: -50,
            id: Math.random(),
            collected: false
          }
        ]);
      }
    }, 800);
    return () => clearInterval(interval);
  }, [gameStarted, gameOver, roadWidth]);

  // Move power-ups
  useEffect(() => {
    if (!gameStarted || gameOver) return;
    const interval = setInterval(() => {
      setPowerUps(old =>
        old.map(p => ({ ...p, y: p.y + speed })).filter(p => p.y < 600)
      );
    }, 50);
    return () => clearInterval(interval);
  }, [speed, gameOver, gameStarted]);

  // Move coins
  useEffect(() => {
    if (!gameStarted || gameOver) return;
    const interval = setInterval(() => {
      setCoinsCollected(old =>
        old.map(c => ({ ...c, y: c.y + speed })).filter(c => c.y < 600)
      );
    }, 50);
    return () => clearInterval(interval);
  }, [speed, gameOver, gameStarted]);

  // Collision detection
  useEffect(() => {
    if (!gameStarted || gameOver || invincible) return;

    // Enemy collision
    chariots.forEach(c => {
      if (Math.abs(c.x - playerX) < 50 && c.y > 450 && c.y < 550) {
        playSound('collision');
        setLives(prev => {
          const newLives = prev - 1;
          if (newLives <= 0) {
            setGameOver(true);
            playSound('gameOver');
          } else {
            setInvincible(true);
            setTimeout(() => setInvincible(false), 1000);
          }
          return newLives;
        });
        setChariots(prev => prev.filter(ch => ch.id !== c.id));
      }
    });

    // Power-up collection
    powerUps.forEach(p => {
      if (Math.abs(p.x - playerX) < 50 && p.y > 450 && p.y < 550) {
        playSound('powerUp');
        if (p.type === 'life') {
          setLives(prev => Math.min(prev + 1, 5));
        } else if (p.type === 'shield') {
          setInvincible(true);
          setTimeout(() => setInvincible(false), 3000);
        }
        setPowerUps(prev => prev.filter(power => power.id !== p.id));
      }
    });

    // Coin collection
    setCoinsCollected(prevCoins => {
      const updatedCoins = prevCoins.map(coin => {
        if (!coin.collected && 
            Math.abs(coin.x - playerX) < 50 && 
            coin.y > 450 && 
            coin.y < 550) {
          playSound('coin');
          setCoins(c => c + 1);
          return { ...coin, collected: true };
        }
        return coin;
      });
      return updatedCoins.filter(coin => !coin.collected);
    });
  }, [chariots, playerX, powerUps, gameStarted, gameOver, invincible]);

  // Flash effect
  useEffect(() => {
    if (!invincible) return;
    const interval = setInterval(() => {
      if (gameRef.current) gameRef.current.classList.toggle('opacity-50');
    }, 200);
    
    return () => {
      clearInterval(interval);
      if (gameRef.current) gameRef.current.classList.remove('opacity-50');
    };
  }, [invincible]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-amber-900 to-amber-700 p-4">
      <h1 className="text-3xl sm:text-4xl font-bold text-yellow-300 mb-3 text-center drop-shadow-lg">
        ⚔️ Mahabharata Rath Yuddha ⚔️
      </h1>
      
      {!gameStarted ? (
        <div className="flex flex-col items-center justify-center bg-amber-800 bg-opacity-80 rounded-2xl p-6 sm:p-8 border-4 border-yellow-600 w-full max-w-sm">
          <h2 className="text-2xl font-bold text-yellow-200 mb-4">Battle of Kurukshetra</h2>
          <p className="text-yellow-100 mb-2 text-center">Use ← → keys to move your chariot</p>
          <p className="text-yellow-100 mb-4 text-center">Collect coins, avoid enemies, gather power-ups!</p>
          <button
            onClick={startGame}
            className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-amber-900 px-6 py-3 rounded-full font-bold text-lg sm:text-xl hover:scale-105 transition-all shadow-lg mb-4"
          >
            Begin the Epic Battle
          </button>
          <div className="mt-4 text-yellow-200 text-sm">
            <p>🏆 Score 500 points to advance in the epic</p>
            <p>🪙 Collect coins to prove your valor</p>
            <p>🛡️ Gather shields for divine protection</p>
            <p>❤️ Collect divine nectar for extra lives</p>
            <p className="mt-2">⚔️ Avoid Kaurava and Asura chariots</p>
          </div>
        </div>
      ) : (
        <div
          ref={gameRef}
          className="relative w-[90vw] max-w-sm h-[70vh] sm:h-[600px] bg-gradient-to-b from-yellow-100 via-orange-200 to-red-300 overflow-hidden border-4 border-yellow-600 rounded-2xl flex justify-center items-end shadow-2xl"
        >
          {/* Road */}
          <div
            ref={roadRef}
            className="absolute top-0 left-1/2 transform -translate-x-1/2 h-full bg-gradient-to-b from-yellow-200 via-orange-100 to-yellow-300"
            style={{ width: `${roadWidth}px` }}
          >
            {/* Mahabharata style road markings */}
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute left-1/2 w-[8px] sm:w-[10px] h-[40px] bg-red-800 opacity-70"
                style={{ top: (i * 100 + (score * speed) % 1000) % 1000 }}
              ></div>
            ))}
            {/* Road borders with ancient design */}
            <div className="absolute left-0 top-0 w-2 h-full bg-red-900"></div>
            <div className="absolute right-0 top-0 w-2 h-full bg-red-900"></div>
          </div>

          {/* Player */}
          <div
            className={`absolute bottom-10 w-[70px] sm:w-[80px] h-[90px] sm:h-[100px] transition-transform duration-100 ${
              invincible ? 'animate-pulse' : ''
            }`}
            style={{ left: playerX - 40 }}
          >
            <img src="/images/avatars/rath.png" alt="Arjuna's Rath" className="w-full h-full object-contain" />
          </div>

          {/* Enemies */}
          {chariots.map(c => (
            <div
              key={c.id}
              className="absolute w-[70px] sm:w-[80px] h-[90px] sm:h-[100px]"
              style={{ left: c.x - 40, top: c.y }}
            >
              {c.enemyType === 'kaurava' ? (
                <img
                  src="/images/avatars/kaurava-chariot.png"
                  alt="Kaurava Chariot"
                  className={`w-full h-full object-contain ${c.type === 'elite' ? 'filter brightness-75' : ''}`}
                />
              ) : (
                <img
                  src="/images/avatars/asura-avatar.png"
                  alt="Asura Chariot"
                  className={`w-full h-full object-contain ${c.type === 'elite' ? 'filter brightness-75' : ''}`}
                />
              )}
            </div>
          ))}

          {/* Power-ups */}
          {powerUps.map(p => (
            <div
              key={p.id}
              className="absolute w-[35px] sm:w-[40px] h-[35px] sm:h-[40px] flex items-center justify-center animate-bounce"
              style={{ left: p.x - 20, top: p.y }}
            >
              {p.type === 'shield' ? <div className="text-3xl">🛡️</div> : <div className="text-3xl">❤️</div>}
            </div>
          ))}

          {/* Coins */}
          {coinsCollected.map(coin => (
            <div
              key={coin.id}
              className="absolute w-[30px] h-[30px] flex items-center justify-center animate-spin"
              style={{ left: coin.x - 15, top: coin.y }}
            >
              <div className="text-2xl">🪙</div>
            </div>
          ))}

          {/* UI */}
          <div className="absolute top-2 left-2 flex gap-2 flex-wrap">
            <div className="text-yellow-900 font-bold text-sm sm:text-lg bg-yellow-200 px-3 py-1 rounded-full shadow-md">
              Score: {score}
            </div>
            <div className="text-yellow-900 font-bold text-sm sm:text-lg bg-yellow-200 px-3 py-1 rounded-full shadow-md">
              Coins: {coins}
            </div>
            <div className="text-yellow-900 font-bold text-sm sm:text-lg bg-yellow-200 px-3 py-1 rounded-full shadow-md">
              Level: {level}
            </div>
          </div>

          {/* Lives */}
          <div className="absolute top-2 right-2 flex gap-1">
            {[...Array(5)].map((_, i) => (
              <div key={i} className={`text-xl sm:text-2xl ${i < lives ? 'text-red-500' : 'text-gray-400'}`}>
                ❤️
              </div>
            ))}
          </div>

          {/* Invincibility indicator */}
          {invincible && (
            <div className="absolute top-12 right-2 text-yellow-800 font-bold bg-yellow-300 px-3 py-1 rounded-full text-sm animate-pulse">
              Divine Protection!
            </div>
          )}

          {/* Game Over Screen */}
          {gameOver && (
            <div className="absolute inset-0 flex flex-col justify-center items-center bg-black bg-opacity-70 text-white text-center z-10">
              <h2 className="text-3xl sm:text-4xl font-bold mb-2 text-yellow-300">⚔️ Epic Battle Concluded ⚔️</h2>
              <p className="text-xl sm:text-2xl mb-2">Final Score: <span className="font-bold text-yellow-300">{score}</span></p>
              <p className="text-xl sm:text-2xl mb-2">Coins Collected: <span className="font-bold text-yellow-300">{coins}</span></p>
              <p className="text-lg sm:text-xl mb-6">Level Reached: <span className="font-bold text-yellow-300">{level}</span></p>
              <button
                onClick={startGame}
                className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-amber-900 px-6 py-3 rounded-full font-bold text-lg sm:text-xl hover:scale-105 transition-all shadow-lg"
              >
                Fight Another Battle
              </button>
            </div>
          )}
        </div>
      )}
      
      {gameStarted && !gameOver && (
        <div className="mt-4 text-yellow-200 text-center bg-amber-800 bg-opacity-50 p-3 rounded-lg w-full max-w-sm text-sm sm:text-base">
          Use ← → keys to move | Collect 🪙 coins | Avoid ⚔️ enemies | Gather 🛡️ & ❤️ power-ups
        </div>
      )}
    </div>
  );
};

export default RathGame;